/**
 * Operator-facing labels — collection manager exception queue (read-only V0).
 */

import type { AgingBucket } from '@domain/finance/finance-receivables-query';
import type {
  CollectionManagerExceptionFlagCode,
  CollectionManagerExceptionSeverity,
} from '@shared/collection/collection-manager-exception-queue-types';
import { COLLECTION_STAGE_RECOMMENDATION_CAVEAT_LABELS } from '@shared/business-workbench/collection-stage-recommendation-operator-labels';

export const COLLECTION_MANAGER_EXCEPTION_QUEUE_OPERATOR_LABELS = {
  pageTitle: '수금 관리 예외 큐',
  pageSubtitle:
    '감독이 확인해야 할 수금 케이스를 우선순위대로 표시합니다. 읽기 전용이며 케이스 조치는 포함하지 않습니다.',
  boundaryCopy:
    '이 큐는 참고용 집계입니다. 검토 확인·보류는 예외 알림에 대한 감독 기록이며, 약속·연락·단계 변경·법적 조치 실행은 수행하지 않습니다.',
  loading: '예외 큐를 불러오는 중…',
  loadFailed: '예외 큐를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  retryAction: '다시 불러오기',
  emptyQueue:
    '감독 확인이 필요한 케이스가 없습니다. (전체 수금 상태가 양호하다는 의미는 아닙니다.)',
  permissionUnavailable: '수금 관리 예외 큐를 볼 권한이 없습니다.',
  filtersTitle: '필터',
  filterFlag: '예외 유형',
  filterSeverity: '심각도',
  filterOverdueBucket: '연체 구간',
  filterSearch: '거래처·케이스 검색',
  filterSearchPlaceholder: '거래처명 또는 케이스 참조',
  filterApply: '적용',
  filterClear: '초기화',
  loadMore: '더 보기',
  loadMoreLoading: '불러오는 중…',
  counterparty: '거래처',
  primaryException: '주요 예외',
  secondaryFlags: '추가 플래그',
  severity: '심각도',
  overdueSummary: '연체 요약',
  brokenCount: '미이행 약속',
  attentionSince: '주의 시작',
  openCase: '케이스 열기',
  legalPrepCaveats: '법무 준비 주의',
  outstandingAmount: '미수 잔액',
  currentStage: '수금 단계',
  emptyValue: '—',
  allFlags: '전체',
  allSeverities: '전체',
  allBuckets: '전체',
} as const;

const FLAG_LABELS: Record<CollectionManagerExceptionFlagCode, string> = {
  broken_commitment: '미이행 약속',
  overdue_bucket: '연체 구간',
  active_legal_prep_recommendation: '법무 준비 권고',
};

const SEVERITY_LABELS: Record<CollectionManagerExceptionSeverity, string> = {
  critical: '긴급',
  high: '높음',
  medium: '보통',
  low: '낮음',
};

const OVERDUE_BUCKET_LABELS: Record<AgingBucket, string> = {
  current: '정상',
  '1-30': '1–30일',
  '31-60': '31–60일',
  '61-90': '61–90일',
  '90+': '90일 초과',
};

export function labelManagerExceptionFlag(
  code: CollectionManagerExceptionFlagCode | string | null | undefined
): string {
  if (!code?.trim()) {
    return COLLECTION_MANAGER_EXCEPTION_QUEUE_OPERATOR_LABELS.emptyValue;
  }
  return FLAG_LABELS[code as CollectionManagerExceptionFlagCode] ?? code;
}

export function labelManagerExceptionSeverity(
  severity: CollectionManagerExceptionSeverity | string | null | undefined
): string {
  if (!severity?.trim()) {
    return COLLECTION_MANAGER_EXCEPTION_QUEUE_OPERATOR_LABELS.emptyValue;
  }
  return SEVERITY_LABELS[severity as CollectionManagerExceptionSeverity] ?? severity;
}

export function labelManagerExceptionOverdueBucket(
  bucket: AgingBucket | string | null | undefined
): string {
  if (!bucket?.trim()) {
    return COLLECTION_MANAGER_EXCEPTION_QUEUE_OPERATOR_LABELS.emptyValue;
  }
  return OVERDUE_BUCKET_LABELS[bucket as AgingBucket] ?? bucket;
}

export function formatManagerExceptionOverdueSummary(
  daysOverdue: number | null,
  bucket: AgingBucket | null
): string {
  const bucketLabel = bucket ? labelManagerExceptionOverdueBucket(bucket) : null;
  if (daysOverdue !== null && Number.isFinite(daysOverdue)) {
    const days = `${daysOverdue}일`;
    return bucketLabel ? `${bucketLabel} · ${days}` : days;
  }
  return bucketLabel ?? COLLECTION_MANAGER_EXCEPTION_QUEUE_OPERATOR_LABELS.emptyValue;
}

export function formatManagerExceptionAmount(amount: string, currency: string): string {
  const parsed = Number(amount);
  if (Number.isFinite(parsed)) {
    const formatted = new Intl.NumberFormat('ko-KR').format(parsed);
    return currency?.trim() ? `${formatted} ${currency.trim()}` : formatted;
  }
  return currency?.trim() ? `${amount} ${currency.trim()}` : amount;
}

export function labelManagerExceptionLegalPrepCaveat(caveatCode: string): string {
  const mapped = COLLECTION_STAGE_RECOMMENDATION_CAVEAT_LABELS[caveatCode];
  return mapped ?? caveatCode;
}

export function formatManagerExceptionAttentionSince(iso: string | null | undefined): string {
  if (!iso?.trim()) {
    return COLLECTION_MANAGER_EXCEPTION_QUEUE_OPERATOR_LABELS.emptyValue;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
